import React, { useEffect, useRef, useCallback } from 'react';

const ShootingStar = () => {
  const canvasRef = useRef(null);
  const starsRef = useRef([]);
  const animationIdRef = useRef(null);
  const nextStarTimeoutRef = useRef(null);
  const mouseRef = useRef({ x: -999, y: -999 });

  const createStar = useCallback((x, y, angle, speed, triggered = false) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const len = triggered ? 180 + Math.random() * 120 : 120 + Math.random() * 180;
    const spd = triggered ? speed * (0.6 + Math.random() * 0.8) : speed;
    const color = triggered
      ? ['#4a5a67', '#99687c', '#c37a53', '#7a9aad'][Math.floor(Math.random() * 4)]
      : Math.random() > 0.5 ? '#4a5a67' : '#99687c';

    starsRef.current.push({
      x, y,
      vx: Math.cos(angle) * spd,
      vy: Math.sin(angle) * spd,
      length: len,
      color,
      alpha: triggered ? 0.4 : 0,
      phase: 'fadein',
      trail: [],
      maxTrailLen: Math.floor(len / spd),
      triggered,
    });
  }, []);

  const spawnBurst = useCallback((cx, cy) => {
    const count = 5 + Math.floor(Math.random() * 4);
    for (let i = 0; i < count; i++) {
      const angle = (Math.PI * 2 * i) / count + (Math.random() - 0.5) * 0.4;
      const speed = 3 + Math.random() * 5;
      createStar(cx, cy, angle, speed, true);
    }
  }, [createStar]);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resize();
    window.addEventListener('resize', resize);

    // Auto shooting stars
    const scheduleNext = () => {
      const delay = 3000 + Math.random() * 6000;
      nextStarTimeoutRef.current = setTimeout(() => {
        const startX = Math.random() * canvas.width * 0.7;
        const startY = Math.random() * canvas.height * 0.4;
        const angle = (Math.PI / 180) * (25 + Math.random() * 20);
        const speed = 6 + Math.random() * 6;
        createStar(startX, startY, angle, speed, false);
        scheduleNext();
      }, delay);
    };
    scheduleNext();

    // Mouse move — leave a faint comet trail when moving fast
    let lastMouse = { x: 0, y: 0, t: 0 };
    const handleMouseMove = (e) => {
      mouseRef.current = { x: e.clientX, y: e.clientY };
      const now = Date.now();
      const dx = e.clientX - lastMouse.x;
      const dy = e.clientY - lastMouse.y;
      const dt = now - lastMouse.t || 1;
      const speed = Math.sqrt(dx * dx + dy * dy) / dt;

      if (speed > 1.2 && Math.random() < 0.04) {
        const angle = Math.atan2(dy, dx);
        createStar(e.clientX, e.clientY, angle, speed * 6, true);
      }
      lastMouse = { x: e.clientX, y: e.clientY, t: now };
    };

    // Click — burst
    const handleClick = (e) => {
      spawnBurst(e.clientX, e.clientY);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('click', handleClick);

    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      starsRef.current = starsRef.current.filter(s => s.alpha > 0 || s.phase === 'fadein');

      starsRef.current.forEach(s => {
        s.trail.push({ x: s.x, y: s.y });
        if (s.trail.length > s.maxTrailLen) s.trail.shift();
        s.x += s.vx;
        s.y += s.vy;

        if (s.phase === 'fadein') {
          s.alpha = Math.min(1, s.alpha + (s.triggered ? 0.15 : 0.08));
          if (s.alpha >= 1) s.phase = 'fadeout';
        } else {
          s.alpha = Math.max(0, s.alpha - (s.triggered ? 0.04 : 0.025));
        }

        if (s.trail.length < 2) return;

        const grad = ctx.createLinearGradient(
          s.trail[0].x, s.trail[0].y,
          s.trail[s.trail.length - 1].x, s.trail[s.trail.length - 1].y
        );
        grad.addColorStop(0, `${s.color}00`);
        grad.addColorStop(0.6, `${s.color}${Math.floor(s.alpha * 80).toString(16).padStart(2, '0')}`);
        grad.addColorStop(1, `${s.color}${Math.floor(s.alpha * 255).toString(16).padStart(2, '0')}`);

        ctx.beginPath();
        ctx.moveTo(s.trail[0].x, s.trail[0].y);
        s.trail.forEach(p => ctx.lineTo(p.x, p.y));
        ctx.strokeStyle = grad;
        ctx.lineWidth = s.triggered ? 1 : 1.5;
        ctx.lineCap = 'round';
        ctx.stroke();

        // Head glow
        const headGrad = ctx.createRadialGradient(s.x, s.y, 0, s.x, s.y, s.triggered ? 3 : 4);
        headGrad.addColorStop(0, `${s.color}${Math.floor(s.alpha * 255).toString(16).padStart(2, '0')}`);
        headGrad.addColorStop(1, `${s.color}00`);
        ctx.beginPath();
        ctx.arc(s.x, s.y, s.triggered ? 3 : 4, 0, Math.PI * 2);
        ctx.fillStyle = headGrad;
        ctx.fill();
      });

      animationIdRef.current = requestAnimationFrame(draw);
    };

    animationIdRef.current = requestAnimationFrame(draw);

    return () => {
      cancelAnimationFrame(animationIdRef.current);
      clearTimeout(nextStarTimeoutRef.current);
      window.removeEventListener('resize', resize);
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('click', handleClick);
    };
  }, [createStar, spawnBurst]);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'fixed',
        inset: 0,
        width: '100vw',
        height: '100vh',
        zIndex: -9,
        pointerEvents: 'none',
        display: 'block',
      }}
    />
  );
};

export default ShootingStar;